import { Checkbox, Link, mergeStyles, PrimaryButton, TextField, Dropdown } from '@fluentui/react';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PageHeader } from '../../../../web/common/components/PageHeader';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import { SMCExperimentQueryMaxCharacter, SourceApps, textAreaColSizeOutlook, textAreaColSizeWindowsCopilotDeepLink } from '../../../models/constants/constants';
import { ServiceRequestPage, ServiceRequestPageType } from '../../../models/reduxState';
import { AttachmentsContainer } from '../containerComponents/AttachmentsContainer';
import { EmailFieldContainer } from '../containerComponents/RequestDetails/EmailFieldContainer';
import { PhoneFieldContainer } from '../containerComponents/RequestDetails/PhoneFieldContainer';
import { TitleFieldContainer } from '../containerComponents/RequestDetails/TitleFieldContainer';
import { UserDescriptionFieldContainer } from '../containerComponents/RequestDetails/UserDescriptionFieldContainer';
import { SeverityContainer } from '../containerComponents/SeverityContainer';
import { ModalityResponseModel, WaitTimeResponseModel, Region } from '../models/modalityModels';
import {
  FreelanceRequest,
  MetaDataKeys,
  RequestModality,
  RaveMetaData,
  RequestSeverity,
  ExtensionAttributeKeys,
} from '../models/requestModels';
import { getRequestConfirmButtonStyles } from '../styles/attachmentsStyles';
import { commonClassNames } from '../styles/commonStyles';
import { IContactUsStyles, getContactUsClassNames } from '../styles/contactUsStyles';
import { getRequestCreationClassNames, getRequestCreationStyles } from '../styles/requestCreationStyles';
import { IStyledComponent } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';
import { InputField, InputType } from '../../../../web/models/typings/inputFields';
import { EduConfig } from '../../../../web/edu/eduConfig';
import { EduToFreelanceRequestMap, getValueFromFreelanceRequest } from '../../../../web/edu/eduToFreelanceRequestMap';
import { EduFormField, EduProductId } from '../../../../web/edu/eduConstants';
import { getDefaultFieldValueFromText } from '../../../../web/edu/eduHelpers';
import { EduSdsTerms } from '../../../../web/edu/EduSdsTerms';

interface IRequestCreationStateProps {
  draftRequest: FreelanceRequest;
  modalityResponse: ModalityResponseModel;
  regionInfo: Array<Region>;
  alchemyThemeRoutingShowWaitTime: boolean;
  isPhoneNumberValid: boolean;
  isEmailValid: boolean;
  isTitleValid: boolean;
  showModalitiesStickyFooter: boolean;
  isUserImpactAndRoleDropdownEnabled: boolean;
  isC2CCountryCodeEnabled: boolean;
  userImpactOptions: string[];
  userRoleOptions: string[];
  isOutlookDotCom: boolean;
  contactSearchedQuery: string;
  isAttachmentsEnabled: boolean;
  isUploadAttachmentInProgress: boolean;
  upsellMetaDataKey: string;
  isSignUp: boolean;
  isUnifiedPremier: boolean;
  isUnifiedEnterprisePremier: boolean;
  isNonUnifiedPremier: boolean;
  premierEntitlementId: string;
  businessImpactConfirmed: boolean;
  customerAvailabilityConfirmed: boolean;
  severity: RequestSeverity;
  isPremierTenantEnabled: boolean;
  sourceApp: SourceApps;
  isAmc: boolean;
  isNonAgave: boolean;
  isBATenant: boolean;
  isBusinessAssistUXEnabled: boolean;
  eduProductId: string;
  additionalFields: InputField[];
  isWindowsCopilotRequestCreationDeeplink: boolean;
  isCMCRequestCreationDeeplink: boolean;
  updateIsPhoneNumberValid: (isPhoneNumberValid: boolean) => void;
  updateIsEmailValid: (isEmailValid: boolean) => void;
  updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
  navigateToPage: (page: ServiceRequestPage) => void;
  navigateBack: () => void;
  dispatchCreateRequest: () => void;
  setContactSearchedQueryText: (query: string, isAutoSuggestQuery: boolean) => void;
  updateDraftRequestWorkSpaceId: (id: string) => void;
  updateSelectedEduProduct: (productId: string) => void;
}

type IRequestCreationProps = IRequestCreationStateProps & IStyledComponent<IContactUsStyles>;

export class RequestCreation extends React.Component<IRequestCreationProps, { isInputAllowed: boolean, isUpsellAgreementChecked: boolean }> {
  private charRemainingSpan: HTMLSpanElement;
  private charRemaining = (SMCExperimentQueryMaxCharacter - this.props.contactSearchedQuery.length);
  private workSpaceId = '';

  public constructor(props: IRequestCreationProps) {
    super(props);
    this.state = {
      isInputAllowed: true,
      isUpsellAgreementChecked: false,
    };
  }

  public componentDidMount = () => {
    // Create workSpaceId
    if (this.props.isAttachmentsEnabled) {
      this.workSpaceId = this.createGUID();
      this.props.updateDraftRequestWorkSpaceId(this.workSpaceId);
    }
    getHelpLogger.logTicketingPageLoaded(`${RequestModality[this.props.draftRequest?.Modality]}Page`);
  };

  public render() {
    const modalityType: RequestModality = this.props.draftRequest.Modality;
    const modality: WaitTimeResponseModel = this.props.modalityResponse.AgentModalities.Modalities
      .filter((waitTimeResponseModel: WaitTimeResponseModel) => waitTimeResponseModel.Modality === modalityType)[0];
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });
    const requestCreationClassNames = getRequestCreationClassNames(getRequestCreationStyles, { isNonAgave: this.props.isNonAgave });
    const requestConfirmButtonStyles = getRequestConfirmButtonStyles({ theme: this.props.theme, withBorderRadius: isGetHelpSourceApp(this.props.sourceApp) });
    const requestAttachmentsProps = {
      workspaceId: this.workSpaceId,
      requestId: '',
      attachments: this.props.draftRequest.Attachments,
      theme: this.props.theme,
    };
    const requestCreationSeverityProps = {
      isUnifiedPremier: this.props.isUnifiedPremier,
      isUnifiedEnterprisePremier: this.props.isUnifiedEnterprisePremier,
      isNonUnifiedPremier: this.props.isNonUnifiedPremier,
      businessImpactConfirmed: this.props.businessImpactConfirmed,
      customerAvailabilityConfirmed: this.props.customerAvailabilityConfirmed,
      severity: this.props.severity,
      theme: this.props.theme,
    };
    const  confirmButtonText = this.props.sourceApp === SourceApps.AMC
      ? getString('amcStrings_confirmButtonText')
      : getString('requestCreation_confirmButtonText');

    if (!getString(`requestModality_${RequestModality[modalityType]}_Title`, true, true, true)) {
      return null;
    }
    return (
      <>
        <PageHeader title={this.props.isAmc ? getString('amcStrings_confirmInformationLabel') : getString('requestConfirmation_confirmInformationLabel')} theme={this.props.theme} />
        <div className={contactUsClassNames.panelBody}>
          <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall)}>
            <div className={contactUsClassNames.modalityOption}>
              {!this.props.showModalitiesStickyFooter && !this.props.isSignUp && !this.props.isAmc && <div className={contactUsClassNames.modalityOptionTitle}>
                {getString(`requestModality_${RequestModality[modalityType]}_Title`)}
              </div>}
              {
                !!modality &&
                this.props.alchemyThemeRoutingShowWaitTime &&
                <div>{getString(`requestModality_${RequestModality[modalityType]}_Description`).replace('{0}', `${modality.WaitTime}`)}</div>
              }
            </div>
            {
              this.props.additionalFields.map(field => (
                <div key={field.id} className={contactUsClassNames.fieldSection}>
                  {this.renderAdditionalField(field)}
                </div>
              ))
            }
            {
              this.props.isUserImpactAndRoleDropdownEnabled &&
              <div>
                <div className={contactUsClassNames.fieldSection}>
                  <div className={contactUsClassNames.fieldLabel}>
                    <span>{getString('requestCreation_userImpactText')}</span>
                    <span className={contactUsClassNames.fieldLabelRequired}> *</span>
                  </div>
                  <div className={contactUsClassNames.fieldFlexLine}>
                    <select
                      id='userImpactDropdown'
                      className={contactUsClassNames.dialingCodeDropdown}
                      onChange={this.onUserImpactDropDownChange}
                    >
                      <option value='' >{getString('requestCreation_dropDownSelectAnOptionText')}</option>
                      {
                        this.props.userImpactOptions.map((impact, index) => {
                          return <option value={impact} key={index}>{impact}</option>;
                        })
                      }
                    </select>
                  </div>
                </div>
                <div className={contactUsClassNames.fieldSection}>
                  <div className={contactUsClassNames.fieldLabel}>
                    <span>{getString('requestCreation_userRoleText')}</span>
                    <span className={contactUsClassNames.fieldLabelRequired}> *</span>
                  </div>
                  <div className={contactUsClassNames.fieldFlexLine}>
                    <select
                      id='userRoleDropdown'
                      className={contactUsClassNames.dialingCodeDropdown}
                      onChange={this.onUserRoleDropDownChange}
                    >
                      <option value='' >{getString('requestCreation_dropDownSelectAnOptionText')}</option>
                      {
                        this.props.userRoleOptions.map((role, index) => {
                          return <option value={role} key={index}>{role}</option>;
                        })
                      }
                    </select>
                  </div>
                </div>
              </div>
            }
            {
              // OutlookDotCom and Copilot deeplink uses a description box here because it doesn't have self-help
              (this.props.isOutlookDotCom || this.props.isWindowsCopilotRequestCreationDeeplink || this.props.isCMCRequestCreationDeeplink) &&
              <div>
                <div className={contactUsClassNames.fieldLabel}>
                  <span>{getString('modality_outlookDotComDescriptionLabel')}</span>
                  <span className={contactUsClassNames.fieldLabelRequired}> *</span>
                </div>
                <textarea
                  maxLength={SMCExperimentQueryMaxCharacter}
                  value={this.props.draftRequest.UserDescription}
                  onChange={this.onRequestCreationUserDescriptionChange}
                  className={mergeStyles('form-control', contactUsClassNames.fieldInputGroup, commonClassNames.resizeNone, contactUsClassNames.requestCreationIssueDescription)}
                  rows={3}
                  cols={this.props.isOutlookDotCom ? textAreaColSizeOutlook : textAreaColSizeWindowsCopilotDeepLink}
                />
                <div className={mergeStyles('col-lg-10', contactUsClassNames.col, contactUsClassNames.caption, 'text-counter', contactUsClassNames.fieldSection)}>
                  <span
                    id='issueDescRemainChar'
                    ref={(node) => (this.charRemainingSpan = node)}
                  >
                    {this.charRemaining}
                  </span>{' '}
                  {getString('search_charRemainingText')}
                </div>
                {!this.props.isOutlookDotCom && <br/>}
              </div>
            }
            {this.props.isPremierTenantEnabled && <SeverityContainer {...requestCreationSeverityProps} />}
            <div className={this.props.isBATenant && this.props.isBusinessAssistUXEnabled && requestCreationClassNames.businessAssistRequestFormContainer}>
              {!!this.props.isBATenant && this.props.isBusinessAssistUXEnabled && (
                <>
                  <TitleFieldContainer />
                  <UserDescriptionFieldContainer />
                </>
              )}
              {
                // Chat or Email
                (modalityType == RequestModality.Chat || modalityType == RequestModality.Email) &&
                <EmailFieldContainer />
              }
              {
                // C2C or Phone
                (modalityType == RequestModality.C2C || modalityType == RequestModality.Phone) &&
                <PhoneFieldContainer setIsInputAllowed={this.setIsInputAllowed} />
              }
            </div>
            {
              (!!this.props.upsellMetaDataKey) &&
              <div className={contactUsClassNames.upsellCheckbox}>
                <Checkbox
                  checked={this.state.isUpsellAgreementChecked}
                  onChange={this.onAgreeToPayUpsellCheckboxClick}
                  label={this.getUpsellAgreementString()}
                />
              </div>
            }
            {this.props.isAttachmentsEnabled && <AttachmentsContainer {...requestAttachmentsProps} />}
            {this.props.sourceApp === SourceApps.Edu && this.props.eduProductId === EduProductId.SchoolDataSync && <EduSdsTerms/>}
            <div>
              <PrimaryButton
                id='createRequestConfirmButton'
                styles={requestConfirmButtonStyles}
                type='button'
                data-bi-bhvr='CONTACT'
                data-bi-area='modalitiesSection'
                data-bi-name='createRequestConfirmButton'
                data-bi-formnm='maxFunnel'
                data-bi-freelancerequest={JSON.stringify({ Modality: this.props.draftRequest.Modality })}
                onClick={this.onCreateRequestConfirmButtonClick}
                text={confirmButtonText}
                disabled={this.isConfirmButtonDisabled()}
              />
              {!this.props.showModalitiesStickyFooter &&
                <Link
                  id='createRequestBackButton'
                  className={commonClassNames.textDecorationNone}
                  data-bi-bhvr='CONTACT'
                  data-bi-area='modalitiesSection'
                  data-bi-name='createRequestBackButton'
                  data-bi-formnm='maxFunnel'
                  data-bi-freelancerequest={JSON.stringify({ Modality: this.props.draftRequest.Modality })}
                  onClick={this.onCreateRequestBackButtonClick}
                >
                  {getString('requestCreation_backButtonText')}
                </Link>}
            </div>
          </div>
        </div>
      </>
    );
  }

  private createGUID = () => {
    const workspaceId = uuidv4();
    return workspaceId;
  };

  private onRequestCreationUserDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!!event && !!event.target) {
      event.stopPropagation();
      const query = event.target.value;
      this.charRemainingSpan.textContent = (SMCExperimentQueryMaxCharacter - (!!query ? query.length : 0)).toString();
      this.props.setContactSearchedQueryText(query, false);
      this.props.updateDraftRequest({
        UserDescription: query,
      });
    }
  };

  private isRequestCreationUserDescriptionInvalid = () => {
    return (this.props.isOutlookDotCom || this.props.isWindowsCopilotRequestCreationDeeplink || this.props.isCMCRequestCreationDeeplink) && this.props.draftRequest.UserDescription.trim() === '';
  };

  private isUpsellAgreementUnchecked = () => {
    return !!this.props.upsellMetaDataKey && !this.state.isUpsellAgreementChecked;
  };

  private onCreateRequestConfirmButtonClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!!event && !!event.target) {
      event.stopPropagation();
    }

    if (this.props.sourceApp === SourceApps.SurfaceApp && window && window.chrome && window.chrome.webview && window.chrome.webview.postMessage) {
      if(this.props.draftRequest.Modality === RequestModality.Chat) {   
        window.chrome.webview.postMessage("ChatSupportRequestCreated");
      } else {
        window.chrome.webview.postMessage("PhoneSupportRequestCreated");
      }
    };
  
    switch (this.props.draftRequest.Modality) {
      case RequestModality.C2C:
      case RequestModality.Phone:
        const index = Number(this.props.draftRequest.UserPhoneDialingCodeIndex);
        const region = (!!this.props.regionInfo &&
          this.props.regionInfo.length > 0 &&
          0 <= index &&
          index < this.props.regionInfo.length)
          ? this.props.regionInfo[index]
          : null;
        const dialingCode = (!!region && !!region.DialingCode) ? region.DialingCode : '';
        let userPhone = `${dialingCode}${this.props.draftRequest.UserPhoneWithoutDialingCode}`;
        const twoDigitCountryCode = !!region && !!region.TwoDigitCountryCode ? region.TwoDigitCountryCode : '';
        if (!!userPhone) {
          // Remove any non-digit chars, '-' will cause c2c to fail
          userPhone = userPhone.replace(/[^0-9]/g, '');
        }

        this.props.updateDraftRequest({
          UserPhone: userPhone,
          // In this scenario the invalid UserEmail could break the AddRequest call, clear UserEmail for C2C.
          // 1. GetAvailableModalitiesV2 returns Email and C2C
          // 2. User selects Email modality and fills in an invalid Email address, which will be updated to draftRequest
          // 3. User switches to C2C modality and fills valid phone number
          // 4. User clicks Confirm button and the AddRequest call fails as the Email is invalid.
          UserEmail: null,
          TwoDigitCountryCode: twoDigitCountryCode,
        });
        break;
      default:
        break;
    }

    // Update Premier Tenant related ExtensionAttributes
    this.updateDraftRequestPremierExtensionAttributes();

    this.props.dispatchCreateRequest();
    const page: ServiceRequestPage = {
      isBackButtonPresent: false,
      serviceRequestPageType: ServiceRequestPageType.RequestConfirmation,
    };
    this.props.navigateToPage(page);
  };

  private updateDraftRequestPremierExtensionAttributes = () => {
    const premierProperties: Partial<FreelanceRequest> = {
      ExtensionAttributes: {},
    };

    if (!!this.props.premierEntitlementId) {
      premierProperties.ExtensionAttributes[ExtensionAttributeKeys.IsPremierUnified] = this.props.isUnifiedPremier.toString();
      premierProperties.ExtensionAttributes[ExtensionAttributeKeys.IsUnifiedEnterprise] = this.props.isUnifiedEnterprisePremier.toString();
      premierProperties.ExtensionAttributes[ExtensionAttributeKeys.PremierEntitlementId] = this.props.premierEntitlementId;
    }

    this.props.updateDraftRequest(premierProperties);
  };

  private isConfirmButtonDisabled = (): boolean => {
    const modalityType: RequestModality = this.props.draftRequest.Modality;
    let isDisabled = false;

    switch (modalityType) {
      case RequestModality.Chat:
        isDisabled =
          !this.props.draftRequest.UserEmail ||
          !this.props.isEmailValid ||
          this.props.isUploadAttachmentInProgress ||
          this.isRequestCreationUserDescriptionInvalid();
        break;
      case RequestModality.C2C:
      case RequestModality.Phone:
        isDisabled =
          !this.props.draftRequest.UserPhoneDialingCodeIndex ||
          !this.props.draftRequest.UserPhoneWithoutDialingCode ||
          !this.props.isPhoneNumberValid ||
          (this.props.isBATenant && !this.props.isTitleValid) ||
          this.props.isUploadAttachmentInProgress ||
          !this.state.isInputAllowed ||
          this.isUpsellAgreementUnchecked() ||
          this.isRequestCreationUserDescriptionInvalid();
        break;
      case RequestModality.Email:
        isDisabled =
          !this.props.draftRequest.UserEmail ||
          !this.props.isEmailValid ||
          (this.props.isBATenant && !this.props.isTitleValid) ||
          this.props.isUploadAttachmentInProgress
        break;
    }
    if (this.props.isUserImpactAndRoleDropdownEnabled) {
      isDisabled = isDisabled || !(this.props.draftRequest.MetaDatas.some((metaData: RaveMetaData) => {
        return metaData.Key === MetaDataKeys.UserImpact && !!metaData.Value;
      }) &&
        this.props.draftRequest.MetaDatas.some((metaData: RaveMetaData) => {
          return metaData.Key === MetaDataKeys.UserRole && !!metaData.Value;
        }));
    }
    if (this.props.isPremierTenantEnabled) {
      isDisabled = isDisabled || ((this.props.severity === RequestSeverity.Critical ||
        this.props.severity === RequestSeverity.Sev1 ||
        this.props.severity === RequestSeverity.SevA) &&
        (!this.props.businessImpactConfirmed || !this.props.customerAvailabilityConfirmed));
    }
    if (this.props.additionalFields.length > 0) {
      const isRequiredFieldMissing = this.props.additionalFields.some(inputField => {
        const { id, isRequired } = inputField;
        const { topLevelKey, metaDataKey, extensionAttributeKey } = EduToFreelanceRequestMap[id] || {};
      
        if (!isRequired || (!topLevelKey && !metaDataKey && !extensionAttributeKey)) {
          return false;
        }

        const hasTopLevelValue = topLevelKey && this.props.draftRequest[topLevelKey];
        const hasExtensionAttribute = extensionAttributeKey && this.props.draftRequest.ExtensionAttributes?.[extensionAttributeKey];
        const hasMetaData = metaDataKey && this.props.draftRequest.MetaDatas?.some(
          metaData => metaData.Key === metaDataKey && metaData.Value
        );
        
        const hasValue = hasTopLevelValue || hasExtensionAttribute || hasMetaData;
        return !hasValue;
      });
      isDisabled ||= isRequiredFieldMissing;
    }
    return isDisabled;
  };

  private onCreateRequestBackButtonClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!!event && !!event.target) {
      event.stopPropagation();
    }

    this.props.navigateBack();
  };

  private onUserImpactDropDownChange = (event: any) => {
    if (!!event && !!event.target) {
      event.preventDefault();
      event.stopPropagation();
      this.props.updateDraftRequest({
        MetaDatas: [{ Key: MetaDataKeys.UserImpact, Value: event.target.value }],
      });
    }
  };

  private onUserRoleDropDownChange = (event: any) => {
    if (!!event && !!event.target) {
      event.preventDefault();
      event.stopPropagation();
      this.props.updateDraftRequest({
        MetaDatas: [{ Key: MetaDataKeys.UserRole, Value: event.target.value }],
      });
    }
  };

  private onAgreeToPayUpsellCheckboxClick = (event?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void => {
    this.setState({ isUpsellAgreementChecked: !!checked });
  };

  private getUpsellAgreementString = () => {
    switch (this.props.upsellMetaDataKey) {
      case MetaDataKeys.IsAgentAssistSubscriptionSignup:
        return getString('m365Upsell_agentAssistSubscriptionAgreement');
      case MetaDataKeys.IsPayPerIncidentExperiment:
        return getString('m365Upsell_payPerIncidentAgreement');
      default:
        return '';
    }
  };

  private setIsInputAllowed = (isInputAllowed: boolean) => {
    if (this.state.isInputAllowed !== isInputAllowed) {
      this.setState({ isInputAllowed: isInputAllowed });
    }
  };

  private updateDraftRequestWithEduData = (propName: string, value: string) => {
    if (!value || value.trim() === '') {
      value = null;
    }

    const delta: Partial<FreelanceRequest> = {};
    const freelanceRequestMapping = EduToFreelanceRequestMap[propName];

    if (freelanceRequestMapping?.topLevelKey) {
      delta[freelanceRequestMapping.topLevelKey] = value;
    }
    if (freelanceRequestMapping?.metaDataKey) {
      delta.MetaDatas = [{ Key: freelanceRequestMapping.metaDataKey, Value: value }];
    }
    if (freelanceRequestMapping?.extensionAttributeKey) {
      delta.ExtensionAttributes = { [freelanceRequestMapping.extensionAttributeKey]: value };
    }

    this.props.updateDraftRequest(delta);
  };

  private renderAdditionalField(inputField: InputField) {
    if (this.props.sourceApp === SourceApps.Edu) {
      return this.renderEduField(inputField);
    }
  }

  private renderEduField = (inputField: InputField) => {
    let onChange: (value: string) => void;
    let defaultValue = null;

    switch (inputField.id) {
      case EduFormField.Product:
        defaultValue = this.props.eduProductId;
        onChange = (value: string) => {
          const productName = EduConfig.products[value].name;
          this.updateDraftRequestWithEduData(inputField.id, productName);
          this.props.updateSelectedEduProduct(value);
        };
        break;
      case EduFormField.Title:
      case EduFormField.Description:
      case EduFormField.LMS:
      case EduFormField.NewSdsCustomer:
      case EduFormField.ProductFeatureArea:
      case EduFormField.UserImpact:
        const draftRequestValue = getValueFromFreelanceRequest(inputField.id, this.props.draftRequest);
        defaultValue = getDefaultFieldValueFromText(inputField.id as EduFormField, draftRequestValue);
        onChange = (value: string) => {
          this.updateDraftRequestWithEduData(inputField.id, value);
        };
        break;
      case EduFormField.Attachments:
      case EduFormField.PreferredEmail:
        return;
    }

    return this.renderInputField(inputField, defaultValue, onChange);
  };

  private renderInputField = (inputField: InputField, defaultValue?: string, onChange?: (value: string) => void) => {
    const label = getString(inputField.labelStringId);

    switch (inputField.inputType) {
      case InputType.Text:
        return (
          <TextField
            label={label}
            ariaLabel={label}
            placeholder={getString(inputField.placeholderStringId)}
            required={inputField.isRequired}
            onChange={(_, newValue) => onChange(newValue)}
            defaultValue={defaultValue}
          />
        );
      case InputType.TextArea:
        return (
          <TextField
            label={label}
            ariaLabel={label}
            placeholder={getString(inputField.placeholderStringId)}
            multiline
            rows={3}
            required={inputField.isRequired}
            onChange={(_, newValue) => onChange(newValue)}
            defaultValue={defaultValue}
          />
        );
      case InputType.Checkbox:
        return (
          <Checkbox 
            label={label}
            ariaLabel={label}
            defaultChecked={defaultValue?.toLowerCase() === 'true'}
            onChange={(_, isChecked) => onChange(isChecked.toString())}
          />
        );
      case InputType.Dropdown:
        const options = inputField.options.map(option => {
          return {
            key: option.value,
            text: option.text ?? getString(option.textStringId),
          };
        });
        return (
          <Dropdown
            label={label}
            ariaLabel={label}
            placeholder={getString(inputField.placeholderStringId)}
            options={options}
            required={inputField.isRequired}
            onChange={(_, option) => onChange(option.key as string)}
            defaultSelectedKey={defaultValue}
          />
        );
    }
  };
}
